import React, { type FC, useEffect } from 'react'
import { useSnackbar } from 'notistack'

import {
    canServiceBeDisabled,
    canServiceBeEnabled,
    Capability,
    ConnectwareError,
    createIsAuthenticatedWithCapabilitiesSelector,
    type CybusDetailedService,
    doesServiceNeedAuthorization,
    Translation,
} from '../../../../domain'

import { useAsyncCallback } from '../../Callback'
import { ConfigureOutlineIcon, DeleteIcon, DownloadIcon, EnableIcon, PauseIcon, UpdateIcon, useStatusTranslator } from '../../common'
import { DetailsActionButton } from '../../Details'
import { useTranslator } from '../../Internationalization'
import { useAppState, useAppUsecases } from '../../State'

const selectCanCreateOrUpdateServices = createIsAuthenticatedWithCapabilitiesSelector(Capability.SERVICES_CREATE_OR_UPDATE)

export const HeaderActions: FC<{ resource: CybusDetailedService }> = ({ resource: service }) => {
    const translator = useTranslator()
    const translateStatus = useStatusTranslator()
    const { enqueueSnackbar } = useSnackbar()
    const canCreateOrUpdateServices = useAppState(selectCanCreateOrUpdateServices)

    const { bulkDisableServicesUsecase, bulkDeleteServicesUsecase, bulkEnableServiceUsecase, downloadServiceUsecase, updateServiceUsecase } = useAppUsecases()
    const [startUpdate, updateLoading] = useAsyncCallback(() => updateServiceUsecase.startUpdate(service), [updateServiceUsecase, service])
    const [startDownload, isDownloading, downloadError] = useAsyncCallback(() => downloadServiceUsecase.invoke(service), [updateServiceUsecase, service])

    const handleServiceEnabledClick = (): void => {
        bulkEnableServiceUsecase.selectServices([service.id])
        if (!doesServiceNeedAuthorization(service)) {
            void bulkEnableServiceUsecase.enable()
        }
    }

    useEffect(() => {
        if (ConnectwareError.is(downloadError)) {
            enqueueSnackbar(translator.formatTranslation(Translation.DOWNLOAD_COMMISSIONING_FILE_DESCRIPTION_ERROR), {
                variant: 'error',
                autoHideDuration: 5_000,
                anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
            })
        }
    }, [downloadError])

    return (
        <>
            {canServiceBeEnabled(service) ? (
                <DetailsActionButton
                    data-testid="enable-service"
                    text={Translation.ENABLE}
                    capabilities={[Capability.SERVICES_MANAGE]}
                    onClick={handleServiceEnabledClick}
                    startIcon={<EnableIcon />}
                    showText
                />
            ) : canServiceBeDisabled(service) ? (
                <DetailsActionButton
                    data-testid="disable-service"
                    text={Translation.DISABLE}
                    capabilities={[Capability.SERVICES_MANAGE]}
                    onClick={() => bulkDisableServicesUsecase.selectServices([service.id])}
                    startIcon={<PauseIcon />}
                    showText
                />
            ) : (
                <DetailsActionButton text={translateStatus(service.status)} data-testid="service-status" disabled startIcon={<PauseIcon />} showText />
            )}
            <DetailsActionButton
                data-testid={canCreateOrUpdateServices ? 'update-or-reconfigure-service' : 'view-service-configuration'}
                text={canCreateOrUpdateServices ? Translation.UPDATE : Translation.VIEW_CONFIGURATION}
                capabilities={[Capability.SERVICES_PARAMETERS_READ, Capability.SERVICE_COMMISSIONING_FILE_READ]}
                disabled={updateLoading}
                loading={updateLoading}
                onClick={startUpdate}
                startIcon={canCreateOrUpdateServices ? <UpdateIcon /> : <ConfigureOutlineIcon />}
            />
            {!service.catalog && (
                <DetailsActionButton
                    data-testid="download-service-commissioning-file"
                    text={Translation.DOWNLOAD_COMMISSIONING_FILE_DESCRIPTION}
                    capabilities={[Capability.SERVICE_COMMISSIONING_FILE_READ]}
                    onClick={startDownload}
                    disabled={isDownloading}
                    loading={isDownloading}
                    startIcon={<DownloadIcon />}
                />
            )}
            <DetailsActionButton
                data-testid="delete-service"
                text={Translation.DELETE}
                capabilities={[Capability.SERVICES_MANAGE]}
                onClick={() => bulkDeleteServicesUsecase.selectServices([service.id])}
                startIcon={<DeleteIcon />}
                variant="dangerOutline"
            />
        </>
    )
}
